import { useEffect, useRef, useState, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { toast } from "sonner";
import {
  Highlighter, Underline, Strikethrough, StickyNote, PanelLeft, PanelBottom, X,
  ZoomIn, ZoomOut, Loader2, Trash2, Pencil, Check, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, getToken, API } from "@/lib/api";
import { HIGHLIGHT_COLORS } from "@/lib/constants";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const TYPE_ICONS = { highlight: Highlighter, underline: Underline, strikethrough: Strikethrough };

export function formatAnnDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function PdfReader({ bookId, initialPage = 1 }) {
  const [book, setBook] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [annotations, setAnnotations] = useState([]);
  const [pageWidth, setPageWidth] = useState(700);
  const [zoom, setZoom] = useState(1);
  const [panel, setPanel] = useState("hidden"); // hidden | left | bottom
  const [pending, setPending] = useState(null);
  const [color, setColor] = useState(HIGHLIGHT_COLORS[0].hex);
  const [annType, setAnnType] = useState("highlight");
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editNote, setEditNote] = useState("");
  const [editColor, setEditColor] = useState(HIGHLIGHT_COLORS[0].hex);
  const [editType, setEditType] = useState("highlight");
  const [hovered, setHovered] = useState(null);
  const hoverTimer = useRef(null);

  const handleAnnEnter = (a, e) => {
    clearTimeout(hoverTimer.current);
    const r = e.currentTarget.getBoundingClientRect();
    setHovered({ ann: a, top: r.top, bottom: r.bottom, left: r.left + r.width / 2 });
  };
  const scheduleHoverClose = () => {
    hoverTimer.current = setTimeout(() => setHovered(null), 220);
  };
  const keepHover = () => clearTimeout(hoverTimer.current);
  const closeHoverNow = () => { clearTimeout(hoverTimer.current); setHovered(null); };

  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const saveTimer = useRef(null);
  const didInitScroll = useRef(false);

  const fileUrl = book ? `${API}/books/${bookId}/file?auth=${getToken()}` : null;
  const isPdf = book && (book.filetype === "pdf" || book.content_type === "application/pdf");
  const isMedia = book && ["mp3", "mp4"].includes(book.filetype);

  const loadAnnotations = useCallback(() => {
    api.get(`/annotations?book_id=${bookId}`).then((r) => setAnnotations(r.data));
  }, [bookId]);

  useEffect(() => {
    api.get(`/books/${bookId}`).then((r) => {
      setBook(r.data);
      setProgress(r.data.progress || 0);
    });
    loadAnnotations();
  }, [bookId, loadAnnotations]);

  // measure container width
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth - 48;
        setPageWidth(Math.min(900, Math.max(320, w)));
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [panel]);

  const onDocLoad = ({ numPages }) => setNumPages(numPages);

  // initial scroll to last page
  useEffect(() => {
    if (numPages && !didInitScroll.current && initialPage > 1) {
      didInitScroll.current = true;
      setTimeout(() => scrollToPage(initialPage), 600);
    }
  }, [numPages, initialPage]);

  const scrollToPage = (p) => {
    const el = scrollRef.current?.querySelector(`[data-page="${p}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleScroll = () => {
    const cont = scrollRef.current;
    if (!cont || !numPages) return;
    const pages = cont.querySelectorAll("[data-page]");
    let current = 1;
    const top = cont.getBoundingClientRect().top;
    pages.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top - top < cont.clientHeight * 0.4) current = +el.dataset.page;
    });
    const pct = Math.round((current / numPages) * 100);
    setProgress(pct);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.post(`/books/${bookId}/progress`, { percent: pct, page: current }).catch(() => {});
    }, 800);
  };

  const onMouseUp = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const text = sel.toString().trim();
    if (!text) return;
    const range = sel.getRangeAt(0);
    const rects = Array.from(range.getClientRects());
    const pageEls = scrollRef.current.querySelectorAll("[data-page]");
    const grouped = {};
    let anchor = null;
    rects.forEach((r) => {
      if (r.width < 1 || r.height < 1) return;
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      for (const el of pageEls) {
        const pr = el.getBoundingClientRect();
        if (cx >= pr.left && cx <= pr.right && cy >= pr.top && cy <= pr.bottom) {
          const page = +el.dataset.page;
          (grouped[page] = grouped[page] || []).push({
            x: (r.left - pr.left) / pr.width,
            y: (r.top - pr.top) / pr.height,
            w: r.width / pr.width,
            h: r.height / pr.height,
          });
          anchor = { x: r.left + r.width / 2, y: r.top };
          break;
        }
      }
    });
    if (Object.keys(grouped).length === 0) return;
    setPending({ grouped, text, anchor });
    setNote("");
  };

  const saveAnnotation = async () => {
    if (!pending) return;
    const entries = Object.entries(pending.grouped);
    for (const [page, prects] of entries) {
      await api.post("/annotations", {
        book_id: bookId,
        page: parseInt(page),
        rects: prects,
        color,
        type: annType,
        text: pending.text,
        note,
      });
    }
    setPending(null);
    setNote("");
    window.getSelection()?.removeAllRanges();
    loadAnnotations();
    toast.success("Annotation saved");
  };

  const deleteAnnotation = async (id) => {
    await api.delete(`/annotations/${id}`);
    loadAnnotations();
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditNote(a.note || "");
    setEditColor(a.color);
    setEditType(a.type);
  };

  const saveEdit = async (id) => {
    await api.put(`/annotations/${id}`, { note: editNote, color: editColor, type: editType });
    setEditingId(null);
    loadAnnotations();
    toast.success("Annotation updated");
  };

  const annByPage = (p) => annotations.filter((a) => a.page === p);

  const PanelContent = (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-800">
        <span className="text-sm font-semibold">Annotations ({annotations.length})</span>
        <button data-testid="close-panel" onClick={() => setPanel("hidden")} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto thin-scrollbar p-2 space-y-2">
        {annotations.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Select text in the book to highlight, underline or add notes.</p>}
        {annotations.map((a) => {
          const Icon = TYPE_ICONS[a.type] || Highlighter;
          const isEditing = editingId === a.id;
          return (
            <div key={a.id} data-testid="panel-ann" className="group p-2 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-indigo-300"
              onClick={() => !isEditing && scrollToPage(a.page)}>
              <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                <span className="flex items-center gap-1"><Icon className="h-3 w-3" style={{ color: a.color }} /> p.{a.page}</span>
                {!isEditing && (
                  <span className="flex items-center gap-1.5">
                    <button data-testid="edit-ann" onClick={(e) => { e.stopPropagation(); startEdit(a); }}
                      className="opacity-0 group-hover:opacity-100 hover:text-indigo-600"><Pencil className="h-3 w-3" /></button>
                    <button data-testid="del-ann" onClick={(e) => { e.stopPropagation(); deleteAnnotation(a.id); }}
                      className="opacity-0 group-hover:opacity-100 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-700 dark:text-slate-200 line-clamp-3 border-l-2 pl-1.5" style={{ borderColor: a.color }}>{a.text}</p>
              {isEditing ? (
                <div className="mt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    {[["highlight", Highlighter], ["underline", Underline], ["strikethrough", Strikethrough]].map(([t, I]) => (
                      <button key={t} onClick={() => setEditType(t)}
                        className={`flex-1 flex items-center justify-center py-1 rounded ${editType === t ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600"}`}>
                        <I className="h-3.5 w-3.5" />
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {HIGHLIGHT_COLORS.map((c) => (
                      <button key={c.hex} onClick={() => setEditColor(c.hex)}
                        className={`h-5 w-5 rounded-full border-2 ${editColor === c.hex ? "border-slate-900 dark:border-white" : "border-transparent"}`}
                        style={{ backgroundColor: c.hex }} />
                    ))}
                    <input type="color" value={editColor} onChange={(e) => setEditColor(e.target.value)} className="h-5 w-5 rounded cursor-pointer" />
                  </div>
                  <Textarea data-testid="edit-ann-note" value={editNote} onChange={(e) => setEditNote(e.target.value)}
                    placeholder="Note…" className="text-xs h-14 resize-none" />
                  <div className="flex justify-end gap-1.5">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setEditingId(null)}>Cancel</Button>
                    <Button data-testid="edit-ann-save" size="sm" className="h-7 px-2 text-xs" onClick={() => saveEdit(a.id)}><Check className="h-3 w-3 mr-1" />Save</Button>
                  </div>
                </div>
              ) : (
                <>
                  {a.note && <p className="mt-1 text-[11px] text-slate-500 flex gap-1"><StickyNote className="h-3 w-3 shrink-0 mt-0.5" />{a.note}</p>}
                  {a.created_at && <p className="mt-1 text-[10px] text-slate-400 flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{formatAnnDate(a.created_at)}</p>}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="h-full flex flex-col bg-slate-100 dark:bg-slate-900">
      {/* toolbar */}
      <div className="flex items-center gap-2 px-3 h-11 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 shrink-0">
        <span className="text-sm font-semibold truncate flex-1">{book?.title}</span>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <div className="w-24 h-1.5 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
            <div className="h-full bg-indigo-500" style={{ width: `${progress}%` }} />
          </div>
          <span data-testid="reader-progress">{progress}%</span>
        </div>
        {isPdf && (
          <>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setZoom((z) => Math.max(0.6, z - 0.15))}><ZoomOut className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setZoom((z) => Math.min(2, z + 0.15))}><ZoomIn className="h-4 w-4" /></Button>
            <Button data-testid="panel-left" variant={panel === "left" ? "default" : "ghost"} size="icon" className="h-8 w-8" onClick={() => setPanel(panel === "left" ? "hidden" : "left")}><PanelLeft className="h-4 w-4" /></Button>
            <Button data-testid="panel-bottom" variant={panel === "bottom" ? "default" : "ghost"} size="icon" className="h-8 w-8" onClick={() => setPanel(panel === "bottom" ? "hidden" : "bottom")}><PanelBottom className="h-4 w-4" /></Button>
          </>
        )}
      </div>

      <div className={`flex-1 min-h-0 flex ${panel === "bottom" ? "flex-col" : "flex-row"}`}>
        {panel === "left" && (
          <div className="w-72 shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">{PanelContent}</div>
        )}

        <div ref={scrollRef} onScroll={handleScroll} onMouseUp={isPdf ? onMouseUp : undefined}
          className="flex-1 min-h-0 overflow-y-auto thin-scrollbar py-6">
          {!book ? (
            <div className="h-full grid place-items-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : isPdf ? (
            <Document file={fileUrl} onLoadSuccess={onDocLoad}
              loading={<div className="grid place-items-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}
              error={<p className="text-center text-red-500 py-20">Failed to load PDF.</p>}>
              {Array.from({ length: numPages }, (_, i) => i + 1).map((p) => (
                <div key={p} data-page={p} className="relative mx-auto mb-4 shadow-md bg-white" style={{ width: pageWidth * zoom }}>
                  <Page pageNumber={p} width={pageWidth * zoom} renderAnnotationLayer={false} renderTextLayer />
                  <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 5 }}>
                    {annByPage(p).map((a) =>
                      a.rects.map((rt, idx) => (
                        <AnnRect key={`${a.id}-${idx}`} rect={rt} color={a.color} type={a.type}
                          onEnter={(e) => handleAnnEnter(a, e)} onLeave={scheduleHoverClose} />
                      ))
                    )}
                  </div>
                </div>
              ))}
            </Document>
          ) : isMedia ? (
            <div className="max-w-2xl mx-auto p-6">
              {book.filetype === "mp4" ? (
                <video data-testid="media-player" controls className="w-full rounded-lg" src={fileUrl} />
              ) : (
                <audio data-testid="media-player" controls className="w-full" src={fileUrl} />
              )}
              <p className="mt-4 text-sm text-slate-500">{book.title} — {book.author}</p>
            </div>
          ) : (
            <div className="max-w-md mx-auto text-center p-10">
              <p className="text-slate-600 dark:text-slate-300">This is a <strong>{book.filetype?.toUpperCase()}</strong> file.</p>
              <p className="text-sm text-slate-400 mt-2">In-app highlighting is available for PDFs. You can download this file to read it.</p>
              <a href={fileUrl} download className="inline-block mt-4 text-indigo-600 hover:underline text-sm">Download {book.filename}</a>
            </div>
          )}
        </div>

        {panel === "bottom" && (
          <div className="h-56 shrink-0 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">{PanelContent}</div>
        )}
      </div>

      {/* floating annotation toolbar */}
      {pending && (
        <div data-testid="ann-toolbar"
          className="fixed z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl p-3 w-72"
          style={{
            left: Math.min(Math.max(12, (pending.anchor?.x || 200) - 144), window.innerWidth - 300),
            top: Math.min((pending.anchor?.y || 200) + 12, window.innerHeight - 240),
          }}>
          <div className="flex items-center gap-1 mb-2">
            {[["highlight", Highlighter], ["underline", Underline], ["strikethrough", Strikethrough]].map(([t, Icon]) => (
              <button key={t} data-testid={`type-${t}`} onClick={() => setAnnType(t)}
                className={`flex-1 flex items-center justify-center py-1.5 rounded-md ${annType === t ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600"}`}>
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 mb-2">
            {HIGHLIGHT_COLORS.map((c) => (
              <button key={c.hex} data-testid={`color-${c.name.toLowerCase()}`} onClick={() => setColor(c.hex)}
                className={`h-6 w-6 rounded-full border-2 ${color === c.hex ? "border-slate-900 dark:border-white" : "border-transparent"}`}
                style={{ backgroundColor: c.hex }} title={c.name} />
            ))}
            <input data-testid="color-custom" type="color" value={color} onChange={(e) => setColor(e.target.value)}
              className="h-6 w-6 rounded-full overflow-hidden cursor-pointer border border-slate-300" />
          </div>
          <Textarea data-testid="ann-note" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note (optional)…" className="text-sm mb-2 h-16 resize-none" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setPending(null); window.getSelection()?.removeAllRanges(); }}>Cancel</Button>
            <Button data-testid="ann-save" size="sm" onClick={saveAnnotation}>Save</Button>
          </div>
        </div>
      )}

      {/* hover popup for existing annotations */}
      {hovered && (
        <HoverAnnotationPopup
          key={hovered.ann.id}
          data={hovered}
          onMouseEnter={keepHover}
          onMouseLeave={scheduleHoverClose}
          onChanged={() => { loadAnnotations(); closeHoverNow(); }}
        />
      )}
    </div>
  );
}

function AnnRect({ rect, color, type, onEnter, onLeave }) {
  const base = {
    position: "absolute",
    left: `${rect.x * 100}%`,
    top: `${rect.y * 100}%`,
    width: `${rect.w * 100}%`,
    height: `${rect.h * 100}%`,
    pointerEvents: "auto",
    cursor: "pointer",
    zIndex: 5,
  };
  const handlers = { onMouseEnter: onEnter, onMouseLeave: onLeave };
  if (type === "underline") {
    return <div style={{ ...base, borderBottom: `2px solid ${color}` }} {...handlers} />;
  }
  if (type === "strikethrough") {
    return (
      <div style={base} {...handlers}>
        <div style={{ position: "absolute", top: "50%", left: 0, right: 0, borderTop: `2px solid ${color}`, pointerEvents: "none" }} />
      </div>
    );
  }
  return <div style={{ ...base, backgroundColor: color, opacity: 0.4, borderRadius: 2, mixBlendMode: "multiply" }} {...handlers} />;
}

function HoverAnnotationPopup({ data, onChanged, onMouseEnter, onMouseLeave }) {
  const { ann, top, bottom, left } = data;
  const [mode, setMode] = useState("menu");
  const [color, setColor] = useState(ann.color);
  const [type, setType] = useState(ann.type);
  const [note, setNote] = useState(ann.note || "");

  const placeBelow = top < 260;
  const style = {
    left: Math.min(Math.max(left, 150), (typeof window !== "undefined" ? window.innerWidth : 1000) - 150),
    top: placeBelow ? bottom + 8 : top - 8,
    transform: placeBelow ? "translateX(-50%)" : "translate(-50%, -100%)",
  };

  const del = async () => { await api.delete(`/annotations/${ann.id}`); onChanged(); };
  const delNote = async () => { await api.put(`/annotations/${ann.id}`, { note: "" }); onChanged(); };
  const saveColor = async () => { await api.put(`/annotations/${ann.id}`, { color, type }); onChanged(); };
  const saveNote = async () => { await api.put(`/annotations/${ann.id}`, { note }); onChanged(); };

  const menuBtn = "flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200";

  return (
    <div data-testid="ann-hover-popup" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
      className="fixed z-[60] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl p-2 w-64" style={style}>
      <p className="text-[11px] text-slate-500 line-clamp-2 border-l-2 pl-1.5 mb-2" style={{ borderColor: ann.color }}>{ann.text}</p>
      {mode === "menu" && (
        <div className="grid grid-cols-2 gap-1.5">
          <button data-testid="hover-edit-highlight" onClick={() => setMode("color")} className={menuBtn}><Highlighter className="h-3.5 w-3.5" /> Edit highlight</button>
          <button data-testid="hover-delete-highlight" onClick={del} className={`${menuBtn} text-red-600`}><Trash2 className="h-3.5 w-3.5" /> Delete highlight</button>
          <button data-testid="hover-edit-note" onClick={() => setMode("note")} className={menuBtn}><Pencil className="h-3.5 w-3.5" /> {ann.note ? "Edit note" : "Add note"}</button>
          <button data-testid="hover-delete-note" onClick={delNote} disabled={!ann.note} className={`${menuBtn} ${ann.note ? "text-red-600" : "opacity-40 cursor-not-allowed"}`}><StickyNote className="h-3.5 w-3.5" /> Delete note</button>
        </div>
      )}
      {mode === "color" && (
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            {[["highlight", Highlighter], ["underline", Underline], ["strikethrough", Strikethrough]].map(([t, I]) => (
              <button key={t} onClick={() => setType(t)} className={`flex-1 flex items-center justify-center py-1 rounded ${type === t ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600"}`}><I className="h-3.5 w-3.5" /></button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {HIGHLIGHT_COLORS.map((c) => (
              <button key={c.hex} onClick={() => setColor(c.hex)} className={`h-5 w-5 rounded-full border-2 ${color === c.hex ? "border-slate-900 dark:border-white" : "border-transparent"}`} style={{ backgroundColor: c.hex }} />
            ))}
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-5 w-5 rounded cursor-pointer" />
          </div>
          <div className="flex justify-end gap-1.5">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setMode("menu")}>Back</Button>
            <Button data-testid="hover-save-highlight" size="sm" className="h-7 px-2 text-xs" onClick={saveColor}><Check className="h-3 w-3 mr-1" />Save</Button>
          </div>
        </div>
      )}
      {mode === "note" && (
        <div className="space-y-2">
          <Textarea data-testid="hover-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note…" className="text-xs h-16 resize-none" />
          <div className="flex justify-end gap-1.5">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setMode("menu")}>Back</Button>
            <Button data-testid="hover-save-note" size="sm" className="h-7 px-2 text-xs" onClick={saveNote}><Check className="h-3 w-3 mr-1" />Save</Button>
          </div>
        </div>
      )}
    </div>
  );
}
